/** Small formatting helpers and per-side display data for the HUD. */
import type { AssetManifest } from '../assets/manifest';
import { assetUrl } from '../assets/manifest';
import type { MatchFighter } from '../game/session';
import { formatNumber } from '../ui/format';

/** What the HUD shows for one side of the fight. */
export interface SideInfo {
  name: string;
  fighterId: string;
  /** The fighter's colour (health bar). */
  color: string;
  /** Two-letter country code, when set. */
  country?: string;
  /** Menu images of the fighter's idle and win faces. */
  idle?: string;
  win?: string;
}

/** The store's fallback name for a player who hasn't picked one (store.ts). */
const UNNAMED = 'You';

export function sideInfo(m: MatchFighter, manifest: AssetManifest, fighterName: string): SideInfo {
  const f = manifest.fighters.find((x) => x.id === m.fighterId);
  return {
    // Without a player name, show the fighter's (the "YOU" pill already says who it is).
    name: m.name && m.name !== UNNAMED ? m.name : fighterName,
    fighterId: m.fighterId,
    color: f?.color ?? '#f5a03c',
    country: m.country,
    idle: f ? assetUrl(f.images.idle) : undefined,
    win: f ? assetUrl(f.images.win) : undefined,
  };
}

/** Round clock: whole seconds rounded up, "0:41". */
export function clockText(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds - 1e-6));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Time into a round (KO time): whole seconds rounded down. */
export function elapsedText(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds + 1e-6));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Damage as the fight counts it (jab 70, health 3,250), so every skill rank shows (D48). */
export function damageText(damage: number): string {
  return formatNumber(Math.round(damage));
}

/** Flag emoji from a two-letter country code (regional indicator symbols). */
export function flagEmoji(country?: string): string | null {
  if (!country || !/^[a-z]{2}$/i.test(country)) return null;
  const up = country.toUpperCase();
  return String.fromCodePoint(0x1f1e6 + up.charCodeAt(0) - 65, 0x1f1e6 + up.charCodeAt(1) - 65);
}

export function countryName(country: string, language: string): string {
  try {
    return (
      new Intl.DisplayNames([language], { type: 'region' }).of(country.toUpperCase()) ?? country
    );
  } catch {
    return country;
  }
}

/**
 * Marker for splitting a translated string around a styled value: U+2063 INVISIBLE SEPARATOR,
 * which never appears in real text.
 */
export const MARK = String.fromCharCode(0x2063);
